import { API_CONFIG } from './config';
import logger from './logger';
import {
  clearAppSessionToken,
  clearLegacyAppSessionTokens,
  getAppSessionCredentials,
  getAppSessionTransport,
  readAppSessionToken,
  readLegacyAppSessionToken,
  storeAppSessionToken,
} from './session';

const SESSION_CHECK_TIMEOUT_MS = 5000;
const INVALID_SESSION_STATUSES = new Set([401, 403]);

export class SessionUnavailableError extends Error {
  readonly cause?: unknown;
  readonly status?: number;
  readonly code?: string;
  readonly user?: Record<string, unknown>;

  constructor(
    message = 'Session service is temporarily unavailable',
    details: {
      cause?: unknown;
      status?: number;
      code?: string;
      user?: Record<string, unknown>;
    } = {},
  ) {
    super(message);
    this.name = 'SessionUnavailableError';
    this.cause = details.cause;
    this.status = details.status;
    this.code = details.code;
    this.user = details.user;
  }
}

export const isSessionUnavailableError = (error: unknown): error is SessionUnavailableError =>
  error instanceof SessionUnavailableError;

const fetchSessionWithTimeout = async (url: string, init: RequestInit): Promise<Response> => {
  const controller = new AbortController();
  const timeoutId = window.setTimeout(() => controller.abort(), SESSION_CHECK_TIMEOUT_MS);

  try {
    return await fetch(url, { ...init, signal: controller.signal });
  } finally {
    window.clearTimeout(timeoutId);
  }
};

const readSessionUser = async (
  response: Response,
  verifiedBearerToken?: string | null,
) => {
  const payload = await response.json();
  const refreshedToken = typeof payload?.sessionToken === 'string'
    ? payload.sessionToken.trim()
    : '';
  if (payload?.success) {
    if (refreshedToken || verifiedBearerToken) {
      storeAppSessionToken(refreshedToken || verifiedBearerToken);
    } else if (getAppSessionTransport() === 'cookie') {
      clearLegacyAppSessionTokens();
    }
  }
  return payload?.success ? payload.user : null;
};

const clearInvalidSession = () => {
  clearAppSessionToken();
};

const readUnavailableError = async (response: Response): Promise<SessionUnavailableError> => {
  let payload: unknown = null;
  try {
    payload = await response.json();
  } catch {
    // Infrastructure responses are not always JSON; status still remains useful.
  }
  const errorPayload = payload && typeof payload === 'object'
    ? payload as Record<string, unknown>
    : null;
  const message = typeof errorPayload?.message === 'string'
    ? errorPayload.message.trim()
    : '';

  return new SessionUnavailableError(
    message || `Session service returned ${response.status}`,
    {
      status: response.status,
      code: typeof errorPayload?.code === 'string' ? errorPayload.code : undefined,
      user: errorPayload?.user && typeof errorPayload.user === 'object'
        ? errorPayload.user as Record<string, unknown>
        : undefined,
    },
  );
};

/**
 * Validate the backend session without treating an offline or unavailable
 * session service as a logout. A fallback token is tried only after a
 * definitive cookie-session rejection and is preserved across network faults.
 */
export const checkSession = async (): Promise<any> => {
  const url = `${API_CONFIG.BACKEND_URL}/api/check-session`;
  const transport = getAppSessionTransport();
  const credentials = getAppSessionCredentials();
  const sessionToken = readAppSessionToken();
  const fallbackToken = transport === 'bearer-tab' ? readLegacyAppSessionToken() : null;
  let cookieRejected = false;

  try {
    const response = await fetchSessionWithTimeout(url, {
      method: 'GET',
      credentials,
      headers: {
        Accept: 'application/json',
        ...(sessionToken ? { Authorization: `Bearer ${sessionToken}` } : {}),
      },
    });

    if (response.ok) {
      const user = await readSessionUser(response, sessionToken);
      if (user) return user;
      cookieRejected = true;
    } else if (INVALID_SESSION_STATUSES.has(response.status)) {
      cookieRejected = true;
    } else {
      throw await readUnavailableError(response);
    }
  } catch (error) {
    if (isSessionUnavailableError(error)) throw error;
    logger.warn('Session check unavailable; preserving the current login token.', error);
    throw new SessionUnavailableError(undefined, { cause: error });
  }

  const fallbackCanRecover = Boolean(
    fallbackToken && (!sessionToken || fallbackToken === sessionToken),
  );

  if (cookieRejected && fallbackCanRecover && fallbackToken) {
    try {
      const response = await fetchSessionWithTimeout(url, {
        method: 'GET',
        credentials,
        headers: {
          Accept: 'application/json',
          Authorization: `Bearer ${fallbackToken}`,
        },
      });

      if (response.ok) {
        const user = await readSessionUser(response, fallbackToken);
        if (user) return user;
      } else if (!INVALID_SESSION_STATUSES.has(response.status)) {
        throw await readUnavailableError(response);
      }
    } catch (error) {
      if (isSessionUnavailableError(error)) throw error;
      logger.warn('Fallback session check unavailable; preserving the token.', error);
      throw new SessionUnavailableError(undefined, { cause: error });
    }
  }

  clearInvalidSession();
  return null;
};
